import { Suspense } from 'react';
import { useInShellHost } from '@/components/layout/shell/shell-host-context';
import i18n from '@/i18n';

/**
 * The React-side loading screen.
 *
 * Deliberately a PIXEL MATCH for the pre-React loader in index.html: that one
 * paints before the bundle downloads, this one takes over once React mounts.
 * Two elements are unavoidable (one is plain HTML, one is React), but the
 * handoff must be invisible — so if you change one, change the other.
 *
 * Same spinner geometry (40px, 3px ring), same colours (via tokens), same
 * copy, same centred layout.
 */
export function PageLoader() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-0 p-6 text-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-[3px] border-border-default border-t-primary-600"
        role="status"
        aria-label={i18n.t('common.loading')}
      />
      <p className="mt-4 text-small text-text-muted">
        {i18n.t('common.loading')}
      </p>
    </div>
  );
}

/**
 * Loader for a page loading INSIDE the shell: the top bar and tab bar stay,
 * and only the content area waits. Same spinner and copy as the full-page one.
 */
export function ContentLoader() {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-24 text-center">
      <div
        className="h-10 w-10 animate-spin rounded-full border-[3px] border-border-default border-t-primary-600"
        role="status"
        aria-label={i18n.t('common.loading')}
      />
      <p className="mt-4 text-small text-text-muted">
        {i18n.t('common.loading')}
      </p>
    </div>
  );
}

/**
 * Suspense for a lazy route.
 *
 * Inside the persistent shell it stands down: the shell owns one boundary
 * around every page. A boundary here would be NEW on each navigation, and a
 * new boundary shows its fallback at once, so opening a page whose code had
 * not downloaded yet blanked the screen. With the shell's boundary already on
 * screen, the router's navigation (a transition) keeps the current page up
 * until the next one is ready, the way a phone app does.
 */
export function Lazy({ children }: { children: React.ReactNode }) {
  const inShell = useInShellHost();
  if (inShell) return <>{children}</>;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}
